import { Controller } from '@hotwired/stimulus';

/**
 * Load a stream when the element becomes visible in the viewport.
 * This is useful for mocking Turbo Frame lazy-loading behaviour
 * (showing a skeleton & loading the real content in its place), where a Turbo Frame
 * isn't possible, such as lazy loading a table row.
 *
 * The url should return a Turbo Stream response that'll get rendered to the page.
 *
 * Set `data-load-stream-immediate-value="true"` to fire the fetch on connect
 * instead of waiting for the element to enter the viewport. Use this when the
 * element is positionally unreliable (e.g. a visually-hidden trigger that may
 * sit below the fold) and lazy loading isn't the point.
 */
export default class extends Controller {
  static values = {
    url: String,
    dispatchEventOnRender: {
      type: String,
      default: 'stream_rendered',
    },
    immediate: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    if (this.immediateValue) {
      this.#loadStream();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.observer.unobserve(this.element);
            this.#loadStream();
          }
        });
      },
      { rootMargin: '200px' }
    );

    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
  }

  async #loadStream() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: 'text/vnd.turbo-stream.html' },
      });

      if (!response.ok) throw new Error(response.statusText);

      const html = await response.text();
      if (!html) return;

      window.Turbo.renderStreamMessage(html);
      this.dispatch(this.dispatchEventOnRenderValue);
    } catch (error) {
      console.error('Load stream controller error:', error);
    }
  }
}
